// src/components/BOLT.js
import React, { useState } from "react";
import SectionTitle from "./shared/SectionTitle";
import resumeData from "../data/resumeData";
import GalleryImage from "./GalleryImage"; // Import the new component
import {
	ChevronRight,
	Zap,
	Monitor,
	Cpu,
	Terminal,
	X,
	ExternalLink,
} from "lucide-react"; // Import ExternalLink

const BOLT = () => {
	const { clubExperience } = resumeData;
	const boltExperience = clubExperience.find(
		(club) => club.organization === "BOLT at Virginia Tech"
	);

	// State for modal (Keep this in the parent component)
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [selectedImage, setSelectedImage] = useState(null);

	const openModal = (image) => {
		setSelectedImage(image);
		setIsModalOpen(true);
	};

	const closeModal = () => {
		setIsModalOpen(false);
		setSelectedImage(null);
	};

	// Projects worked on at BOLT
	const boltProjects = [
		{
			name: "Custom Distributed BMS",
			description:
				"Wrote complete firmware for and helped design a custom distributed Battery Management System for BOLT VI using the L9963E Battery Monitoring IC. The system monitors cell voltages and temperatures across the entire battery pack to ensure safe operation.",
			technologies: ["Embedded C", "CAN Bus", "STM32"],
			icon: <Cpu className="text-amber-600" size={24} />,
			iconBg: "bg-amber-50 ring-1 ring-amber-100",
			bgColor: "bg-amber-50",
			textColor: "text-amber-700 ring-1 ring-amber-100",
		},
		{
			name: "BMS Monitoring GUI",
			description:
				"Developed a graphical user interface for BOLT VI's custom BMS that connects over USB-CDC, allowing the team to monitor battery cell status, diagnose issues, and configure system parameters in real-time.",
			technologies: ["Python", "USB-CDC", "GUI Development"],
			icon: <Monitor className="text-emerald-600" size={24} />,
			iconBg: "bg-emerald-50 ring-1 ring-emerald-100",
			bgColor: "bg-emerald-50",
			textColor: "text-emerald-700 ring-1 ring-emerald-100",
		},
		{
			name: "Rider Dashboard Interface",
			description:
				"For BOLT VS, I created a custom dashboard for the motorcycle rider that displays critical information such as speed, battery status, temperature readings, and system warnings in a clear, easy-to-read format.",
			technologies: [
				"UI/UX Design",
				"Embedded Systems",
				"LCD Programming",
			],
			icon: <Zap className="text-violet-600" size={24} />,
			iconBg: "bg-violet-50 ring-1 ring-violet-100",
			bgColor: "bg-violet-50",
			textColor: "text-violet-700 ring-1 ring-violet-100",
		},
		{
			name: "Motorcycle ECU Programming",
			description:
				"In freshman year, I reaprogrammed the microcontroller for the old control PCB on BOLT IV, focusing on implementing CAN to comminicate with the rest of the bike and user logs",
			technologies: ["C/C++", "CAN Bus", "MSP432"],
			icon: <Terminal className="text-brand-600" size={24} />,
			iconBg: "bg-brand-50 ring-1 ring-brand-100",
			bgColor: "bg-brand-50",
			textColor: "text-brand-700 ring-1 ring-brand-100",
		},
	];

	// Gallery Images Data (Keep this here or move to resumeData.js if preferred)
	const galleryImages = [
		{
			id: "bolt1", // Add unique IDs if needed for keys
			src: "/images/bolt-image-1.jpeg",
			alt: "BOLT motorcycle on display",
			description: "BOLT VI sitting in our bay.",
		},
		{
			id: "bolt2",
			src: "/images/bolt-image-2.jpg",
			alt: "Testing of our distributed BMS",
			description:
				"Testing multi-node comminucation with our custom distributed BMS.",
		},
		{
			id: "bolt3",
			src: "/images/bolt-image-3.jpg",
			alt: "Testing our BMS with a 50V battery pack",
			description:
				"Testing our custom BMS with a battery pack for the bike.",
		},
		{
			id: "bolt4",
			src: "/images/bolt-image-4.png",
			alt: "Screenshot of our BMS GUI",
			description:
				"Screenshot of our BMS GUI, showing the current status of the bike.",
		},
	];

	return (
		<section className="max-w-6xl mx-auto">
			{/* BOLT Introduction */}
			<SectionTitle>BOLT at Virginia Tech</SectionTitle>
			<div className="bg-white border border-slate-200/80 shadow-card rounded-2xl overflow-hidden mb-12">
				<div className="flex flex-col md:flex-row">
					<div className="md:w-2/3 p-8 md:p-10">
						<p className="mb-6 text-lg leading-relaxed text-slate-600">
							BOLT is Virginia Tech's electric motorcycle design
							team, focused on engineering, building, and racing
							high-performance electric motorcycles. As a
							multidisciplinary team of engineers, we design
							everything from the custom battery packs to the
							motor controller systems.
						</p>
						<p className="mb-6 text-lg leading-relaxed text-slate-600">
							Our flagship motorcycle runs at 600V and has reached
							a top speed of 160mph. The team competes in electric
							AHRMA's Formula Lightning class, racing against
							other universities and professional teams. We also
							test our bike against a professional rider with a
							Yamaha R1M to see how we stack up against top of the
							line ICE motorcycles.
						</p>
						<p className="text-lg leading-relaxed text-slate-600">
							Being part of BOLT has provided me with invaluable
							hands-on experience in electrical systems design,
							battery management systems, embedded programming,
							and practical engineering problem-solving in a team
							environment.
						</p>
					</div>
					<div className="md:w-1/3 bg-slate-50 border-t md:border-t-0 md:border-l border-slate-200/70 p-8 md:p-10 flex flex-col justify-center space-y-6">
						<div>
							<h3 className="text-xs font-semibold uppercase tracking-[0.14em] text-brand-600 mb-3">
								My Role
							</h3>
							<p className="font-semibold text-lg text-slate-900">
								{boltExperience.position}
							</p>
							<p className="text-slate-600">
								{boltExperience.organization}
							</p>
							<p className="text-slate-500">
								{boltExperience.period}
							</p>
						</div>
						<div>
							<a
								href="https://www.vtbolt.com"
								target="_blank"
								rel="noopener noreferrer"
								className="inline-flex items-center px-4 py-2.5 bg-brand-600 text-white font-medium rounded-xl shadow-xs shadow-brand-600/30 hover:bg-brand-700 hover:-translate-y-0.5 transition-all duration-300"
							>
								Visit BOLT Website
								<ExternalLink className="ml-2" size={16} />
							</a>
						</div>
					</div>
				</div>
			</div>

			{/* BOLT Projects */}
			<SectionTitle>My Projects at BOLT</SectionTitle>
			<div className="grid md:grid-cols-2 gap-6 lg:gap-8 mb-12">
				{boltProjects.map((project, index) => (
					<div
						key={index}
						className="bg-white border border-slate-200/80 shadow-card rounded-2xl p-8 transition-all duration-300 hover:shadow-lift hover:-translate-y-1"
					>
						<div className="flex items-center mb-5">
							<div
								className={`w-12 h-12 rounded-xl ${project.iconBg} flex items-center justify-center mr-4`}
							>
								{project.icon}
							</div>
							<h3 className="text-xl font-bold text-slate-900">
								{project.name}
							</h3>
						</div>
						<p className="mb-6 text-slate-600 leading-relaxed">
							{project.description}
						</p>
						<div className="flex flex-wrap gap-2">
							{project.technologies.map((tech, idx) => (
								<span
									key={idx}
									className={`${project.bgColor} ${project.textColor} px-3 py-1 rounded-full text-sm font-medium`}
								>
									{tech}
								</span>
							))}
						</div>
					</div>
				))}
			</div>

			{/* Achievements and Responsibilities */}
			<SectionTitle>Responsibilities & Achievements</SectionTitle>
			<div className="bg-white border border-slate-200/80 shadow-card rounded-2xl p-8 md:p-10 mb-12">
				<ul className="space-y-4">
					{boltExperience.achievements.map((achievement, index) => (
						<li key={index} className="flex items-start text-slate-600">
							<ChevronRight
								className="text-brand-500 mt-1 mr-3 flex-shrink-0"
								size={20}
							/>
							<span className="text-lg">{achievement}</span>
						</li>
					))}
					<li className="flex items-start text-slate-600">
						<ChevronRight
							className="text-brand-500 mt-1 mr-3 flex-shrink-0"
							size={20}
						/>
						<span className="text-lg">
							Collaborated with a diverse team of engineers across
							electrical, mechanical, and software disciplines
						</span>
					</li>
					<li className="flex items-start text-slate-600">
						<ChevronRight
							className="text-brand-500 mt-1 mr-3 flex-shrink-0"
							size={20}
						/>
						<span className="text-lg">
							Participated in design reviews and technical
							presentations to sponsors and faculty advisors
						</span>
					</li>
					<li className="flex items-start text-slate-600">
						<ChevronRight
							className="text-brand-500 mt-1 mr-3 flex-shrink-0"
							size={20}
						/>
						<span className="text-lg">
							Mentored new team members on embedded systems
							programming and battery management concepts
						</span>
					</li>
				</ul>
			</div>

			{/* Gallery */}
			<SectionTitle>BOLT Gallery</SectionTitle>
			<div className="bg-white border border-slate-200/80 shadow-card rounded-2xl p-8 mb-12">
				<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
					{galleryImages.map((image) => (
						<GalleryImage
							key={image.id || image.src} // Use a unique key
							image={image}
							onImageClick={openModal} // Pass the openModal function
						/>
					))}
				</div>
				{/* Optional: Placeholder text if needed */}
				<div className="text-center mt-6">
					<p className="text-slate-500 text-sm">
						Click on an image to view details.
					</p>
				</div>
			</div>

			{/* Modal (Keep this rendering logic in the parent) */}
			{isModalOpen && selectedImage && (
				<div
					className="fixed inset-0 bg-slate-950/70 backdrop-blur-xs flex items-center justify-center z-50 p-4 animate-fade-in"
					onClick={closeModal} // Close modal on overlay click
				>
					<div
						className="bg-white rounded-2xl shadow-lift max-w-3xl w-full relative p-4 animate-scale-in"
						onClick={(e) => e.stopPropagation()} // Prevent closing when clicking inside modal content
					>
						<button
							onClick={closeModal}
							className="absolute top-3 right-3 text-slate-500 hover:text-slate-900 z-10 bg-white/90 ring-1 ring-slate-200 rounded-full p-1.5 transition-colors"
							aria-label="Close modal"
						>
							<X size={22} />
						</button>
						<div className="max-h-[80vh] overflow-auto">
							<img
								src={selectedImage.src}
								alt={selectedImage.alt}
								className="w-full h-auto object-contain rounded-xl mb-4 max-h-[65vh]"
							/>
							<p className="text-center text-slate-600 px-4 pb-2">
								{selectedImage.description}
							</p>
						</div>
					</div>
				</div>
			)}
		</section>
	);
};

export default BOLT;
